import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  InfoCard,
  DetailItem,
  DetailGrid,
  Badge,
  Button,
  Input,
  Toggle,
  LoadingState,
  ErrorState,
  PageHeader,
} from '../../components';
import { planService } from '../../services/planService';
import {
  CheckCircledIcon,
  CrossCircledIcon,
  CalendarIcon,
  LightningBoltIcon,
  ChatBubbleIcon,
  StarIcon,
  PersonIcon,
  RocketIcon,
  EyeNoneIcon,
  MixIcon,
  Pencil1Icon,
} from '@radix-ui/react-icons';
import { formatDate, formatPrice, getDurationLabel } from '../../utils/formatters';
import { CUSTOM_PLAN_NAME } from './types/subscription';
import './PlanDetail.scss';

// Types matching the API response structure
interface PriceValue {
  amount: number | null;
  taxPercent: number | null;
}

interface RegionalPrice {
  IN: PriceValue;
  US: PriceValue;
  CA: PriceValue;
  EU: PriceValue;
}

interface ApiRegionalPrice {
  India?: PriceValue | number;
  US?: PriceValue | number;
  Canada?: PriceValue | number;
  EU?: PriceValue | number;
  IN?: PriceValue | number;
  CA?: PriceValue | number;
  _i_n?: PriceValue | number;
  _u_s?: PriceValue | number;
  _c_a?: PriceValue | number;
  _e_u?: PriceValue | number;
}

interface RegionalPricePayload {
  _i_n: { amount: number; taxPercent: number };
  _u_s: { amount: number; taxPercent: number };
  _c_a: { amount: number; taxPercent: number };
  _e_u: { amount: number; taxPercent: number };
}

interface PlanVariantDetail {
  planDetailId: number;
  durationMonths: number;
  price: RegionalPrice;
  connects: number | null;
  advanceSearch: boolean;
  limitedSupport: boolean;
  limitedChat: boolean;
  limitedChatDays: number;
  highlightedProfile: boolean;
  astroMatchmaking: boolean;
  dedicatedManager: boolean;
  dedicatedSeniorManager: boolean;
  arrangedMeetings: boolean;
  hideProfile: boolean;
  createdAt: string;
  updatedAt: string;
}

// Region display configuration using country codes
const REGION_CONFIG: {
  key: keyof RegionalPrice;
  apiKey: keyof ApiRegionalPrice;
  payloadKey: keyof RegionalPricePayload;
  label: string;
  currency: string;
  symbol: string;
}[] = [
  { key: 'IN', apiKey: 'India', payloadKey: '_i_n', label: 'India', currency: 'INR', symbol: '₹' },
  { key: 'US', apiKey: 'US', payloadKey: '_u_s', label: 'US', currency: 'USD', symbol: '$' },
  { key: 'CA', apiKey: 'Canada', payloadKey: '_c_a', label: 'Canada', currency: 'CAD', symbol: 'C$' },
  { key: 'EU', apiKey: 'EU', payloadKey: '_e_u', label: 'EU', currency: 'EUR', symbol: '€' },
];

const DEFAULT_PRICE_VALUE: PriceValue = {
  amount: null,
  taxPercent: null,
};

const toNullableNumber = (input: unknown): number | null => {
  if (typeof input === 'number') {
    return Number.isFinite(input) ? input : null;
  }

  if (typeof input === 'string') {
    const normalized = input.trim();
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const toPriceValue = (input: unknown): PriceValue => {
  const directNumber = toNullableNumber(input);
  if (directNumber !== null) {
    return { amount: directNumber, taxPercent: 0 };
  }

  if (input && typeof input === 'object') {
    const value = input as {
      amount?: unknown;
      taxPercent?: unknown;
      tax_percent?: unknown;
      tax?: unknown;
    };
    return {
      amount: toNullableNumber(value.amount),
      taxPercent: toNullableNumber(value.taxPercent ?? value.tax_percent ?? value.tax),
    };
  }

  return { ...DEFAULT_PRICE_VALUE };
};

const mapApiPriceToForm = (price?: ApiRegionalPrice): RegionalPrice => {
  const source = (price ?? {}) as Record<string, unknown>;

  const pickRegionValue = (keys: string[]): unknown => {
    for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null) {
        return source[key];
      }
    }
    return undefined;
  };

  return {
    IN: toPriceValue(pickRegionValue(['India', 'IN', '_i_n', 'india', 'in'])),
    US: toPriceValue(pickRegionValue(['US', '_u_s', 'us'])),
    CA: toPriceValue(pickRegionValue(['Canada', 'CA', '_c_a', 'canada', 'ca'])),
    EU: toPriceValue(pickRegionValue(['EU', '_e_u', 'eu'])),
  };
};

const mapFormPriceToPayload = (price: RegionalPrice): RegionalPricePayload => ({
  _i_n: {
    amount: price.IN.amount ?? 0,
    taxPercent: price.IN.taxPercent ?? 0,
  },
  _u_s: {
    amount: price.US.amount ?? 0,
    taxPercent: price.US.taxPercent ?? 0,
  },
  _c_a: {
    amount: price.CA.amount ?? 0,
    taxPercent: price.CA.taxPercent ?? 0,
  },
  _e_u: {
    amount: price.EU.amount ?? 0,
    taxPercent: price.EU.taxPercent ?? 0,
  },
});
  
interface PlanDetailData {
  planId: string;
  name: string;
  description: string;
  isHidden: boolean;
  isFree: boolean;
  isDynamic: boolean;
  isPopular: boolean;
  createdAt: string;
  updatedAt: string;
  variants: PlanVariantDetail[];
}

// Feature configuration with icons and labels
const FEATURE_CONFIG = [
  { key: 'advanceSearch', label: 'Advanced Search', icon: <MixIcon /> },
  { key: 'limitedSupport', label: 'Limited Support', icon: <ChatBubbleIcon /> },
  { key: 'limitedChat', label: 'Limited Chat', icon: <ChatBubbleIcon /> },
  { key: 'highlightedProfile', label: 'Highlighted Profile', icon: <StarIcon /> },
  { key: 'astroMatchmaking', label: 'Astro Matchmaking', icon: <RocketIcon /> },
  { key: 'dedicatedManager', label: 'Dedicated Manager', icon: <PersonIcon /> },
  { key: 'dedicatedSeniorManager', label: 'Senior Manager', icon: <PersonIcon /> },
  { key: 'arrangedMeetings', label: 'Arranged Meetings', icon: <CalendarIcon /> },
  { key: 'hideProfile', label: 'Hide Profile', icon: <EyeNoneIcon /> },
] as const;

// Feature Badge Component (View Mode)
const FeatureBadge: React.FC<{ enabled: boolean; label: string; icon: React.ReactNode }> = ({
  enabled,
  label,
  icon,
}) => (
  <div className={`plan-detail-feature ${enabled ? 'plan-detail-feature--enabled' : 'plan-detail-feature--disabled'}`}>
    <span className="plan-detail-feature__icon">{icon}</span>
    <span className="plan-detail-feature__label">{label}</span>
    <span className="plan-detail-feature__status">
      {enabled ? <CheckCircledIcon /> : <CrossCircledIcon />}
    </span>
  </div>
);

// Helper to format regional price
const formatRegionalPrice = (amount: number | null, symbol: string): string => {
  return `${symbol}${(amount ?? 0).toLocaleString()}`;
};

// Variant Card Component (View Mode)
const VariantCard: React.FC<{ variant: PlanVariantDetail }> = ({ variant }) => {
  return (
    <div className="plan-detail-variant">
      <div className="plan-detail-variant__header">
        <div className="plan-detail-variant__duration">
          <CalendarIcon />
          <span>{getDurationLabel(variant.durationMonths)}</span>
        </div>
        {/* <div className="plan-detail-variant__price">
          <span className="plan-detail-variant__price-value">{formatPrice(primaryPrice)}</span>
          <span className="plan-detail-variant__price-per-month">
            {formatPrice(pricePerMonth)}/month
          </span>
        </div> */}
      </div>

      {/* Regional Prices */}
      <div className="plan-detail-variant__regional-prices">
        <h4 className="plan-detail-variant__regional-prices-title">Regional Pricing</h4>
        <div className="plan-detail-variant__regional-prices-grid">
          {REGION_CONFIG.map(({ key, label, symbol }) => {
            const amount = variant.price?.[key]?.amount ?? 0;
            const taxPercent = variant.price?.[key]?.taxPercent ?? 0;
            const taxAmount = (amount * taxPercent) / 100;
            const total = amount + taxAmount;

            return (
            <div key={key} className="plan-detail-variant__regional-price">
              <span className="plan-detail-variant__regional-price-label">{label}</span>
              <span className="plan-detail-variant__regional-price-value">
                {formatRegionalPrice(amount, symbol)} + {taxPercent}% tax
              </span>
              <span className="plan-detail-variant__regional-price-subtext">
                Tax: {formatRegionalPrice(taxAmount, symbol)} | Total: {formatRegionalPrice(total, symbol)}
              </span>
            </div>
          );
          })}
        </div>
      </div>

      <div className="plan-detail-variant__stats">
        <div className="plan-detail-variant__stat">
          <LightningBoltIcon />
          <span className="plan-detail-variant__stat-value">{variant.connects}</span>
          <span className="plan-detail-variant__stat-label">Connects</span>
        </div>
        {variant.limitedChat && variant.limitedChatDays > 0 && (
          <div className="plan-detail-variant__stat">
            <ChatBubbleIcon />
            <span className="plan-detail-variant__stat-value">{variant.limitedChatDays}</span>
            <span className="plan-detail-variant__stat-label">Chat Days</span>
          </div>
        )}
      </div>

      <div className="plan-detail-variant__features">
        <h4 className="plan-detail-variant__features-title">Features</h4>
        <div className="plan-detail-variant__features-grid">
          {FEATURE_CONFIG.map(({ key, label, icon }) => (
            <FeatureBadge
              key={key}
              enabled={variant[key as keyof PlanVariantDetail] as boolean}
              label={label}
              icon={icon}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Editable Variant Card Component (Edit Mode)
interface VariantFieldErrors {
  [key: string]: string | undefined;
  price?: string;
  connects?: string;
  limitedChatDays?: string;
}

interface EditableVariantCardProps {
  variant: PlanVariantDetail;
  onChange: (updated: PlanVariantDetail) => void;
  errors?: VariantFieldErrors;
}

const EditableVariantCard: React.FC<EditableVariantCardProps> = ({ variant, onChange, errors }) => {
  const handleFieldChange = (field: keyof PlanVariantDetail, value: any) => {
    onChange({ ...variant, [field]: value });
  };

  const handlePriceChange = (region: keyof RegionalPrice, field: keyof PriceValue, value: string) => {
    const newPrice = {
      ...variant.price,
      [region]: {
        ...variant.price[region],
        [field]: value === '' ? null : Number(value),
      },
    };
    onChange({ ...variant, price: newPrice });
  };

  const primaryPrice = variant.price?.IN?.amount ?? 0;
  const pricePerMonth = primaryPrice / variant.durationMonths;
  return (
    <div className="plan-detail-variant plan-detail-variant--editing">
      <div className="plan-detail-variant__edit-header">
        <h4>{getDurationLabel(variant.durationMonths)} Variant</h4>
        <div className="plan-detail-variant__edit-price">
          {formatPrice(primaryPrice)}
          <span className="plan-detail-variant__edit-price-sub">
            ({formatPrice(pricePerMonth)}/mo)
          </span>
        </div>
      </div>

      <div className="plan-detail-variant__form-grid">
        <Input
          label="Duration (months)"
          type="number"
          value={variant.durationMonths}
          onChange={(e) => handleFieldChange('durationMonths', parseInt(e.target.value) || 1)}
          min={1}
        />
        <Input
          label="Connects"
          type="number"
          value={variant.connects ?? ''}
          onChange={(e) => handleFieldChange('connects', e.target.value === '' ? null : parseInt(e.target.value))}
          min={0}
          error={errors?.connects}
        />
        <Input
          label="Limited Chat Days"
          type="number"
          value={variant.limitedChatDays ?? 0}
          onChange={(e) => handleFieldChange('limitedChatDays', e.target.value === '' ? 0 : parseInt(e.target.value))}
          min={0}
          error={errors?.limitedChatDays}
        />
      </div>

      {/* Regional Pricing Inputs */}
      <div className="plan-detail-variant__pricing-section">
        <h4 className="plan-detail-variant__pricing-title">Regional Pricing</h4>
        <div className="plan-detail-variant__form-grid plan-detail-variant__form-grid--4">
          {REGION_CONFIG.map(({ key, label, symbol }) => {
            const amount = variant.price?.[key]?.amount ?? 0;
            const taxPercent = variant.price?.[key]?.taxPercent ?? 0;

            const taxAmount = ((amount ?? 0) * (taxPercent ?? 0)) / 100;
            const total = (amount ?? 0) + taxAmount;

            return (
              <div key={key} className="plan-detail-variant__region-block">
                <h5 className="plan-detail-variant__region-title">{label} ({symbol})</h5>
                <Input
                  label="Amount"
                  type="number"
                  value={amount ?? ''}
                  onChange={(e) => handlePriceChange(key, 'amount', e.target.value)}
                  min={0}
                  step="0.01"
                  error={errors?.[`amount${key}`]}
                />
                <Input
                  label="Tax %"
                  type="number"
                  value={taxPercent ?? ''}
                  onChange={(e) => handlePriceChange(key, 'taxPercent', e.target.value)}
                  min={0}
                  step="0.01"
                  error={errors?.[`taxPercent${key}`]}
                />
                <div className="plan-detail-variant__region-preview">
                  Tax: {formatRegionalPrice(taxAmount, symbol)} | Total: {formatRegionalPrice(total, symbol)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="plan-detail-variant__features plan-detail-variant__features--editing">
        <h4 className="plan-detail-variant__features-title">Features</h4>
        <div className="plan-detail-variant__features-toggles">
          {FEATURE_CONFIG.map(({ key, label }) => (
            <Toggle
              key={key}
              label={label}
              checked={variant[key as keyof PlanVariantDetail] as boolean}
              onChange={(checked) => handleFieldChange(key as keyof PlanVariantDetail, checked)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Validation errors interface
interface ValidationErrors {
  name?: string;
  description?: string;
  variants?: Record<string, VariantFieldErrors>;
}

// Main PlanDetail Component
export const PlanDetail: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<PlanDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  
  // Edit state
  const [editedPlan, setEditedPlan] = useState<Partial<PlanDetailData>>({});
  const [editedVariants, setEditedVariants] = useState<Record<string, PlanVariantDetail>>({});

  const handleBack = () => navigate('/plans');

  const fetchPlan = useCallback(async () => {
    if (!planId) {
      setError('Plan ID is required');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const planData = await planService.getPlanById(planId) as unknown as PlanDetailData;
      const mappedPlan: PlanDetailData = {
        ...planData,
        isDynamic: planData.isDynamic ?? false,
        variants: (planData.variants || []).map((variant) => ({
          ...variant,
          price: mapApiPriceToForm(variant.price as unknown as ApiRegionalPrice),
        })),
      };
      setPlan(mappedPlan);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch plan details';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // Enter edit mode
  const handleStartEditing = useCallback(() => {
    if (!plan) return;
    const isDynamic = plan.isDynamic ?? false;
    setEditedPlan({
      name: isDynamic ? CUSTOM_PLAN_NAME : plan.name,
      description: plan.description,
      isHidden: isDynamic ? true : (plan.isHidden ?? false),
      isFree: isDynamic ? false : (plan.isFree ?? false),
      isDynamic,
      isPopular: plan.isPopular ?? false,
    });
    const variantsMap: Record<string, PlanVariantDetail> = {};
    plan.variants.forEach((v) => {
      variantsMap[v.planDetailId] = { 
        ...v,
        price: mapApiPriceToForm(v.price as unknown as ApiRegionalPrice),
        limitedChatDays: v.limitedChatDays ?? 0,
      };
    });
    setEditedVariants(variantsMap);
    setIsEditing(true);
  }, [plan]);

  // Cancel editing
  const handleCancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditedPlan({});
    setEditedVariants({});
    setValidationErrors({});
  }, []);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    // Validate plan name
    if (!editedPlan.name?.trim()) {
      errors.name = 'Plan name is required';
      isValid = false;
    }

    // Validate description
    if (!editedPlan.description?.trim()) {
      errors.description = 'Description is required';
      isValid = false;
    }

    // Validate variants
    const variantErrors: Record<string, VariantFieldErrors> = {};
    
    Object.entries(editedVariants).forEach(([variantId, variantData]) => {
      const variantError: VariantFieldErrors = {};
      
      if (variantData.connects === undefined || variantData.connects === null || variantData.connects < 0) {
        variantError.connects = 'Connects is required';
        isValid = false;
      }

      if (variantData.limitedChatDays === undefined || variantData.limitedChatDays === null) {
        variantError.limitedChatDays = 'Limited chat days is required';
        isValid = false;
      }

      if (Object.keys(variantError).length > 0) {
        variantErrors[variantId] = variantError;
      }
    });

    if (Object.keys(variantErrors).length > 0) {
      errors.variants = variantErrors;
    }

    setValidationErrors(errors);
    return isValid;
  }, [editedPlan, editedVariants]);

  // Save changes
  const handleSave = useCallback(async () => {
    if (!plan || !planId) return;
    
    // Validate before saving
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Build plan_details from edited variants
      const planDetails = Object.entries(editedVariants).map(([variantId, variantData]) => ({
        planDetailId: parseInt(variantId),
        price: mapFormPriceToPayload(variantData.price),
        connects: variantData.connects || 0,
        advanceSearch: variantData.advanceSearch ?? false,
        limitedSupport: variantData.limitedSupport ?? false,
        limitedChat: variantData.limitedChat ?? false,
        limitedChatDays: variantData.limitedChatDays ?? 0,
        highlightedProfile: variantData.highlightedProfile ?? false,
        astroMatchmaking: variantData.astroMatchmaking ?? false,
        dedicatedManager: variantData.dedicatedManager ?? false,
        dedicatedSeniorManager: variantData.dedicatedSeniorManager ?? false,
        arrangedMeetings: variantData.arrangedMeetings ?? false,
        hideProfile: variantData.hideProfile ?? false,
      }));
      // Update plan with all data including variants
      const isDynamic = plan.isDynamic ?? false;
      await planService.updatePlan(planId, {
        name: isDynamic ? CUSTOM_PLAN_NAME : editedPlan.name,
        description: editedPlan.description,
        is_hidden: isDynamic ? true : editedPlan.isHidden,
        is_popular: editedPlan.isPopular,
        ...(isDynamic ? { is_free: false } : {}),
        planDetails: planDetails,
      });

      // Refresh plan data
      await fetchPlan();
      setIsEditing(false);
      setEditedPlan({});
      setEditedVariants({});
      setValidationErrors({});
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [plan, planId, editedPlan, editedVariants, fetchPlan, validateForm]);

  // Update variant in edit state
  const handleVariantChange = useCallback((variantId: number, updated: PlanVariantDetail) => {
    setEditedVariants((prev) => ({
      ...prev,
      [variantId]: updated,
    }));
  }, []);

  if (isLoading) {
    return (
      <div className="plan-detail-page">
        <LoadingState message="Loading plan details..." />
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div className="plan-detail-page">
        <ErrorState message={error} onRetry={handleBack} retryLabel="Go Back" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="plan-detail-page">
        <ErrorState message="Plan not found" onRetry={handleBack} retryLabel="Go Back" />
      </div>
    );
  }

  // Sort variants by duration
  const sortedVariants = [...plan.variants].sort((a, b) => a.durationMonths - b.durationMonths);
  const isDynamicPlan = plan.isDynamic ?? false;
  const displayName = isEditing
    ? (isDynamicPlan ? CUSTOM_PLAN_NAME : editedPlan.name || plan.name)
    : plan.name;
  const displayDescription = isEditing ? editedPlan.description || plan.description : plan.description;
  const displayIsHidden = isEditing
    ? (isDynamicPlan ? true : editedPlan.isHidden ?? plan.isHidden)
    : plan.isHidden;
  const displayIsFree = isDynamicPlan
    ? false
    : isEditing
      ? editedPlan.isFree ?? plan.isFree
      : plan.isFree;
  const displayIsPopular = isEditing ? editedPlan.isPopular ?? plan.isPopular : plan.isPopular;

  return (
    <div className="plan-detail-page">
      <PageHeader
        title=""
        backButton={{ label: 'Back to Plans', onClick: handleBack }}
        actions={
          isEditing ? (
            <div className="plan-detail-actions">
              <Button variant="secondary" onClick={handleCancelEditing} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          ) : (
            <Button variant="primary" style={{ display: 'flex', alignItems: 'center' }} onClick={handleStartEditing}>
              <Pencil1Icon style={{ marginRight: '8px' }} /> Edit Plan
            </Button>
          )
        }
      />

      {error && (
        <div className="plan-detail-error">
          {error}
        </div>
      )}

      {Object.keys(validationErrors).length > 0 && (
        <div className="plan-detail-error">
          Please fix the validation errors before saving.
        </div>
      )}

      <div className="plan-detail-content">
        {/* Plan Header Card */}
        <div className={`plan-detail-hero ${isEditing ? 'plan-detail-hero--editing' : ''}`}>
          <div className="plan-detail-hero__content">
            {isEditing ? (
              <>
                <Input
                  label="Plan Name"
                  value={isDynamicPlan ? CUSTOM_PLAN_NAME : editedPlan.name || ''}
                  onChange={(e) => {
                    if (isDynamicPlan) return;
                    setEditedPlan({ ...editedPlan, name: e.target.value });
                  }}
                  placeholder="Enter plan name"
                  className="plan-detail-hero__input"
                  error={validationErrors.name}
                  disabled={isDynamicPlan}
                  readOnly={isDynamicPlan}
                />
                <Input
                  label="Description"
                  value={editedPlan.description || ''}
                  onChange={(e) => setEditedPlan({ ...editedPlan, description: e.target.value })}
                  placeholder="Enter description"
                  error={validationErrors.description}
                  className="plan-detail-hero__input"
                />
                <div className="plan-detail-hero__toggles">
                  <Toggle
                    label="Hidden from users"
                    checked={isDynamicPlan ? true : Boolean(editedPlan.isHidden)}
                    disabled={isDynamicPlan}
                    onChange={(checked) => {
                      if (isDynamicPlan) return;
                      setEditedPlan({ ...editedPlan, isHidden: checked });
                    }}
                  />
                  <Toggle
                    label="Popular Plan"
                    checked={editedPlan.isPopular || false}
                    onChange={(checked) => setEditedPlan({ ...editedPlan, isPopular: checked })}
                  />
                </div>
                {isDynamicPlan && (
                  <p className="plan-detail-hero__description" style={{ color: '#334155', fontSize: '0.875rem' }}>
                    Saving does not change users who already have this plan assigned. The next assignment uses the new values.
                  </p>
                )}
              </>
            ) : (
              <>
                <h1 className="plan-detail-hero__name">{displayName}</h1>
                <p className="plan-detail-hero__description">{displayDescription}</p>
                <div className="plan-detail-hero__badges">
                  <Badge variant={displayIsHidden ? 'warning' : 'success'}>
                    {displayIsHidden ? 'Hidden' : 'Visible'}
                  </Badge>
                  <Badge variant={displayIsFree ? 'info' : 'primary'}>
                    {displayIsFree ? 'Free' : 'Paid'}
                  </Badge>
                  {isDynamicPlan && (
                    <Badge variant="primary">{CUSTOM_PLAN_NAME}</Badge>
                  )}
                  {displayIsPopular && (
                    <Badge variant="success">Popular</Badge>
                  )}
                  <Badge variant="default">
                    {plan.variants.length} Variant{plan.variants.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
              </>
            )}
          </div>
          {!isEditing && (
            <div className="plan-detail-hero__meta">
              <div className="plan-detail-hero__meta-item">
                <span className="plan-detail-hero__meta-label">Created</span>
                <span className="plan-detail-hero__meta-value">{formatDate(plan.createdAt)}</span>
              </div>
              <div className="plan-detail-hero__meta-item">
                <span className="plan-detail-hero__meta-label">Updated</span>
                <span className="plan-detail-hero__meta-value">{formatDate(plan.updatedAt)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Plan Information (View Mode Only) */}
        {!isEditing && (
          <InfoCard title="Plan Information" subtitle="Basic plan details and settings">
            <DetailGrid columns={2}>
              <DetailItem
                icon={<StarIcon />}
                label="Plan Name:"
                value={plan.name}
              />
              <DetailItem
                icon={<ChatBubbleIcon />}
                label="Description:"
                value={plan.description}
              />
              <DetailItem
                icon={<EyeNoneIcon />}
                label="Visibility:"
                value={displayIsHidden ? 'Hidden from users' : 'Visible to users'}
              />
              <DetailItem
                icon={<LightningBoltIcon />}
                label="Plan Type:"
                value={displayIsFree ? 'Free Plan' : 'Paid Plan'}
              />
            </DetailGrid>
          </InfoCard>
        )}

        {/* Pricing Variants */}
        <InfoCard
          title={isEditing ? 'Edit Variants' : 'Pricing Variants'}
          subtitle={isEditing 
            ? 'Modify pricing and features for each variant' 
            : `${plan.variants.length} pricing option${plan.variants.length !== 1 ? 's' : ''} available`
          }
        >
          <div className={`plan-detail-variants ${isEditing ? 'plan-detail-variants--editing' : ''}`}>
            {isEditing
              ? sortedVariants.map((variant) => (
                  <EditableVariantCard
                    key={variant.planDetailId}
                    variant={editedVariants[variant.planDetailId] || variant}
                    onChange={(updated) => handleVariantChange(variant.planDetailId, updated)}
                    errors={validationErrors.variants?.[String(variant.planDetailId)]}
                  />
                ))
              : sortedVariants.map((variant) => (
                  <VariantCard key={variant.planDetailId} variant={variant} />
                ))
            }
          </div>
        </InfoCard>

        {/* Pricing Comparison Table (View Mode Only) */}
        {!isEditing && plan.variants.length > 1 && (
          <InfoCard title="Quick Comparison" subtitle="Compare all variants at a glance">
            <div className="plan-detail-comparison">
              <table className="plan-detail-comparison__table">
                <thead>
                  <tr>
                    <th>Duration</th>
                    {REGION_CONFIG.map(({ key, label, symbol }) => (
                      <th key={key}>{label} ({symbol})</th>
                    ))}
                    <th>Connects</th>
                    <th>Chat Days</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVariants.map((variant) => (
                    <tr key={variant.planDetailId}>
                      <td>{getDurationLabel(variant.durationMonths)}</td>
                      {REGION_CONFIG.map(({ key, symbol }) => (
                        <td key={key} className="plan-detail-comparison__price">
                          {formatRegionalPrice(variant.price?.[key]?.amount ?? 0, symbol)}
                        </td>
                      ))}
                      <td>{variant.connects}</td>
                      <td>{variant.limitedChat ? variant.limitedChatDays : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </InfoCard>
        )}
      </div>
    </div>
  );
};
